import { CdkStepper } from '@angular/cdk/stepper';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  LoginNameFormModel,
  NewPasswordFormModel,
  TokenFormModel,
} from '@app/auth/models/reset-password-form-model';
import { ResetPasswordService } from '@app/auth/services/reset-password.service';
import { NotificationService } from '@app/core/services/notification.service';
import { RouterService } from '@app/routing/router.service';
import { ErrorTransformerService } from '@app/shared/services/error-transformer.service';
import { ErrorDescription } from '@app/utils/error/error-description';
import { PortalError } from '@app/utils/error/portal-error';
import { StringMap } from '@app/utils/string-map';
import { finalize } from 'rxjs/operators';
import { FormGroupTyped } from 'src/types/typed-forms';

@Component({
  selector: 'portal-reset-password',
  templateUrl: './reset-password.component.html',
  styles: [],
  standalone: false,
})
export class ResetPasswordComponent implements OnDestroy, OnInit {
  @ViewChild(CdkStepper, { static: false })
  stepper: CdkStepper;

  loading: boolean = false;

  newPasswordForm: FormGroupTyped<NewPasswordFormModel> = NewPasswordFormModel.formGroup();
  tokenForm: FormGroupTyped<TokenFormModel> = TokenFormModel.formGroup();
  usernameForm: FormGroupTyped<LoginNameFormModel> = LoginNameFormModel.formGroup();

  error?: PortalError | ErrorDescription;
  selectedIndex: number = 0;

  matrixParameters: StringMap<string>;

  constructor(
    private errorTransformerService: ErrorTransformerService,
    private notificationService: NotificationService,
    private resetPasswordService: ResetPasswordService,
    private activatedRoute: ActivatedRoute,
    private routerService: RouterService
  ) {}

  ngOnDestroy(): void {}

  ngOnInit() {
    const { matrixParameters } = this.routerService.extractParameters();
    this.matrixParameters = matrixParameters;

    const loginName = matrixParameters.u;
    const token = matrixParameters.t;
    const step = matrixParameters.s;

    if (loginName !== undefined) {
      if (token !== undefined) {
        // Set data derived from url
        this.tokenForm.setValue({
          loginName,
          token,
        });

        this.usernameForm.setValue({
          loginName,
        });

        // Go to step 2 (the timeout is because the stepper is not yet available in ngOnInit)
        setTimeout(() => {
          this.stepper.next();

          // And submit
          this.submitToken();
        }, 0);
      } else {
        this.usernameForm.setValue({
          loginName,
        });
        if (step === 'token') {
          this.tokenForm.patchValue({
            loginName,
          });
          setTimeout(() => {
            this.stepper.next();
          });
        } else if (step?.length > 0) {
          delete this.matrixParameters.s;
          this.routerService.setMatrixParams(this.activatedRoute, this.matrixParameters);
        }
      }
      this.usernameForm.markAsDirty();
    }
  }

  goBack() {
    this.error = undefined;

    if (this.stepper.selected.state === 'token') {
      this.tokenForm.patchValue({
        token: null,
      });
      delete this.matrixParameters.t;
      delete this.matrixParameters.s;
      this.routerService.setMatrixParams(this.activatedRoute, this.matrixParameters);

      this.stepper.previous();
    }
  }

  goToEnterAuthCode() {
    this.tokenForm.patchValue(this.usernameForm.value);

    this.stepper.next();
    this.routerService.setMatrixParams(this.activatedRoute, {
      ...this.matrixParameters,
      s: 'token',
    });
  }

  submitPassword() {
    this.error = undefined;
    this.loading = true;

    this.resetPasswordService
      .resetPassword(
        this.newPasswordForm.value.loginName,
        this.newPasswordForm.value.newPassword,
        this.newPasswordForm.value.token
      )
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        error: (error) => {
          this.error = this.errorTransformerService.describeError(error, {
            401: 'resetPassword.err.loginName',
            403: 'resetPassword.invalidtoken',
          });
        },
        next: () => {
          this.stepper.next();
        },
      });
  }

  /**
   * Check the token
   */
  submitToken() {
    this.error = undefined;
    this.loading = true;

    this.resetPasswordService
      .verifyToken(this.tokenForm.value.loginName, this.tokenForm.value.token)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        error: (error) => {
          this.error = this.errorTransformerService.describeError(error, {
            401: 'resetPassword.err.loginName',
            403: 'resetPassword.invalidtoken',
          });

          // username is unknown
          if (error.cause instanceof HttpErrorResponse && error.cause.status === 401) {
            this.goBack();
          }
        },
        next: () => {
          this.newPasswordForm.patchValue(this.tokenForm.value);

          this.stepper.next();
          this.routerService.setMatrixParams(this.activatedRoute, {
            u: this.tokenForm.value.loginName,
            t: this.tokenForm.value.token,
          });
        },
      });
  }

  submitUsername() {
    this.error = undefined;
    this.loading = true;

    if (this.usernameForm.valid) {
      this.resetPasswordService
        .initiateResetPassword(this.usernameForm.value.loginName)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          error: (error: PortalError) => {
            this.error = this.errorTransformerService.describeError(error, {
              401: 'resetPassword.err.loginName',
              403: 'resetPassword.err.loginName',
            });
          },
          next: (response) => {
            this.notificationService.showSuccess(
              'resetPassword.authCode.sent' + (response.method ? '.' + response.method : '')
            );
            this.tokenForm.patchValue(this.usernameForm.value);

            this.routerService.setMatrixParams(this.activatedRoute, {
              ...this.matrixParameters,
              s: 'token',
            });
            this.stepper.next();
          },
        });
    }
  }
}
